<script setup lang="ts">
import { ref, computed, h, type VNode } from 'vue'
import ImageComparisonSlider from './ImageComparisonSlider.vue'

interface Props {
  content: string
}

const props = defineProps<Props>()

// Define types for the parsed content structure
interface ComparisonNode {
  type: 'comparison'
  beforeImage: string | undefined
  afterImage: string | undefined
}

interface TextNode {
  type: 'text'
  content: string
}

interface ElementNode {
  type: 'element'
  tag: string
  attrs: Record<string, string>
  children: ParsedContent[]
}

type ParsedContent = ComparisonNode | TextNode | ElementNode | null

// Parse the content and create a mixed array of content
const parsedContent = computed<ParsedContent[]>(() => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = props.content

  // Function to process nodes recursively
  function processNode(node: Node): ParsedContent {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element

      if (element.classList.contains('image-comparison')) {
        return {
          type: 'comparison',
          beforeImage: element.getAttribute('data-before') || undefined,
          afterImage: element.getAttribute('data-after') || undefined
        }
      }

      // Process children recursively
      const children = Array.from(element.childNodes).map((child) => processNode(child))

      // Create element with its original attributes
      const attrs: Record<string, string> = {}
      for (const attr of Array.from(element.attributes)) {
        attrs[attr.name] = attr.value
      }

      return {
        type: 'element',
        tag: element.tagName.toLowerCase(),
        attrs,
        children: children.filter(Boolean) as ParsedContent[]
      }
    }
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      return {
        type: 'text',
        content: node.textContent
      }
    }
    // Handle other node types or empty text nodes
    return null
  }

  return Array.from(tempDiv.childNodes)
    .map((node) => processNode(node))
    .filter(Boolean) as ParsedContent[]
})

// Render function to create the virtual DOM
function renderContent(content: ParsedContent | ParsedContent[]): VNode | string | (VNode | string | null)[] | null {
  if (!content) return null

  if (Array.isArray(content)) {
    return content.map((item) => renderContent(item)) as (VNode | string | null)[]
  }

  if (content.type === 'text') {
    return content.content
  }

  if (content.type === 'comparison' && content.beforeImage && content.afterImage) {
    return h(ImageComparisonSlider, {
      beforeImage: content.beforeImage,
      afterImage: content.afterImage,
      height: '300px'
    })
  }

  if (content.type === 'element') {
    return h(
      content.tag,
      content.attrs,
      content.children.map((child: ParsedContent) => renderContent(child))
    )
  }

  return null
}

// Define the render function
const render = () => {
  return renderContent(parsedContent.value)
}
</script>

<template>
  <component :is="render" />
</template>

<style scoped>
:deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

:deep(ul) {
  padding-left: 20px;
}

:deep(h6) {
  font-size: 1.25rem;
  margin-top: 0;
  margin-bottom: 16px;
  font-weight: bold;
}

:deep(.image-comparison) {
  margin: 20px 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  height: 300px;
}

:deep(.image-comparison-placeholder) {
  display: none;
}
</style>
